import { Mail, Plus, RefreshCw, ArrowRight, RotateCcw, ArrowDownUp, ListChecks, CheckCircle2, Send, Eye, Key, Clock, ShieldAlert } from "lucide-react";
import {
  PageHeading, Section, Callout,
  Diagram, MockTable,
} from "../shared";

export default function NotificationsSection() {
  return (
    <div data-testid="guide-notifications">
      <PageHeading
        icon={Mail}
        title="Email Notifications"
        description="The system sends emails at the important moments in an item's life, so the right people always know what's happening. Email is optional — it only works once an IT Admin has set up email sending (SMTP)."
      />

      <Callout variant="info" title="In plain words">
        You don't have to keep refreshing the system to stay in the loop. When something that affects you happens — a new item, an update, a handoff, a reminder — the system can drop an email into your inbox automatically.
      </Callout>

      <Section title="When does this matter to me?">
        <p className="text-sm text-white/65 leading-relaxed">
          These emails arrive on their own — you don't switch anything on. This page simply lists every moment an email goes out, so you know what to expect and why you received one.
        </p>
      </Section>

      <Section title="When emails are sent">
        <Diagram
          title="The moments that trigger an email"
          frameLabel="Email notifications"
          legend={[
            <>Things that happen <span className="text-white">to your items</span> — new item, update, handoff, return, status change.</>,
            <>Things about <span className="text-white">sub-tasks and approvals</span> — sub-task created/completed, MD approval, dispatch.</>,
            <>Things on a <span className="text-white">timer</span> — reminders before and after the due date, and password resets.</>,
          ]}
        >
          <MockTable
            columns={[
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">1</span>
                Item events
              </span>,
              "Who gets the email",
            ]}
            rows={[
              ["New item registered", "The assigned person"],
              ["Progress update added", "The assigned person"],
              ["Handed off", "The receiving person"],
              ["Returned for amendments", "The assigned person"],
            ]}
          />
          <MockTable
            columns={[
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>
                Sub-tasks & approvals
              </span>,
              "Who gets the email",
            ]}
            rows={[
              ["Sub-task created", "The sub-assignee"],
              ["Sub-task completed", "The parent item's owner"],
              ["MD approves above-MD item", "SDF PMO + SDF Board Secretary"],
              ["Marked dispatched", "The SDF MD"],
            ]}
          />
          <MockTable
            columns={[
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">3</span>
                On a timer
              </span>,
              "When",
            ]}
            rows={[
              ["Pre-due reminders", "Default 14 / 7 / 1 working days before due"],
              ["Past-due reminders", "3, 6, 9, 12 working days after due"],
              ["Password reset link", "When you ask; link lasts 30 minutes"],
            ]}
          />
        </Diagram>

        <div className="space-y-2.5 text-xs text-white/60 mt-4">
          <Row icon={Plus} color="text-green-400" name="Registration">When a new action item is created, the assigned person is emailed the details and instruction.</Row>
          <Row icon={RefreshCw} color="text-cyan-400" name="Progress Update">When an admin or member adds a progress note, the assigned person gets the update.</Row>
          <Row icon={ArrowRight} color="text-[#FF9F43]" name="Handoff">When responsibility is handed to someone new, the receiver gets the item details, your handoff note, and (if filled) the optional "due back by" date and "why this date" explanation.</Row>
          <Row icon={ShieldAlert} color="text-orange-400" name="Item Reassigned (override)">When an admin reassigns an item that someone else was holding, the displaced holder gets a neutral "Item Reassigned" email with a 1-working-day prompt to share anything they had. The reason is <em>not</em> revealed — that conversation is meant to happen in person.</Row>
          <Row icon={RotateCcw} color="text-purple-400" name="Return for Amendments">When an item is sent back for rework, the assigned person is emailed the reason.</Row>
          <Row icon={ArrowDownUp} color="text-indigo-400" name="Status Change (optional)">When an admin changes status with the notification toggle on, the assigned person is told.</Row>
          <Row icon={ListChecks} color="text-[#FF9F43]" name="Sub-Task Assignment">When a sub-task is created, the sub-assignee gets a dedicated email with the parent context.</Row>
          <Row icon={CheckCircle2} color="text-green-400" name="Sub-Task Completion">When a sub-task is marked complete, the parent item's owner is told.</Row>
          <Row icon={Send} color="text-yellow-300" name="MD Approval (above-MD lanes)">When the SDF MD approves an above-MD item, SDF PMO and SDF Board Secretary are told it's now in Awaiting Dispatch.</Row>
          <Row icon={Send} color="text-indigo-400" name="Mark Dispatched">When the PMO marks an item dispatched, the SDF MD is told the upward send is done.</Row>
          <Row icon={Clock} color="text-cyan-400" name="Pre-due reminders">Owners and current holders get reminder emails on the days set by IT under <span className="text-white">Admin → Reminder Schedule</span> (default 14 / 7 / 1 working days before due). Up to 10 reminder days can be set.</Row>
          <Row icon={Clock} color="text-red-400" name="Past-due reminders">Once an item is past due, overdue reminders go out on a fixed rhythm — <span className="text-white">3, 6, 9, and 12 working days</span> after the due date. This rhythm is fixed; only the pre-due days are adjustable.</Row>
          <Row icon={ShieldAlert} color="text-amber-400" name="Stalled-task review (escalation)">When a user reassigns an item using the "Escalate as dropped-the-ball" reason, the named senior reviewer gets an email titled <span className="font-mono text-white">"Stalled-task review requested: …"</span> pointing them to the Escalations Inbox to Confirm or Dismiss.</Row>
          <Row icon={Key} color="text-blue-400" name="Password reset">When you ask to reset a forgotten password, you get an email titled <span className="font-mono text-white">"Password reset request"</span> with a one-time link valid for 30 minutes. The link uses the system's official web address.</Row>
        </div>
      </Section>

      <Section title="Meeting details on emails">
        <p className="text-xs text-white/55 leading-relaxed">
          Every email above automatically includes a <span className="text-white">Meeting</span> row in the details when the item is linked to a meeting. The Registration email goes further: when a meeting task is registered in bulk, it also shows <span className="text-white">Logged By</span> and <span className="text-white">Sent By</span> — the latter only when it differs from Logged By, so readers can see who prepared the minutes versus who pressed Submit.
        </p>
      </Section>

      <Section>
        <div className="flex items-start gap-2">
          <Eye className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
          <p className="text-xs text-white/55">
            <span className="text-white font-medium">Email record:</span> every email attempt — sent or failed — is saved in the item's history, including who was emailed, whether it arrived, and why if it didn't.
          </p>
        </div>
        <Callout variant="info">
          Emails go to people whose team-member name is matched to a user account with an email address on file. If there's no email address, the system simply skips that person and notes why.
        </Callout>
      </Section>
    </div>
  );
}

function Row({ icon: Icon, color, name, children }: { icon: any; color: string; name: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`w-3.5 h-3.5 ${color} mt-0.5 shrink-0`} />
      <p><span className="text-white font-medium">{name}:</span> {children}</p>
    </div>
  );
}
